import { Fragment, useState } from "react";
import { TieOutBadge } from "../components/TieOutBadge.tsx";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.ts";
import { downloadCsv, fmtNum } from "../lib/csv.ts";
import { DemandExplainer } from "../components/DemandExplainer.tsx";
import { CATEGORIES, DemandTable, TAB_LABEL, shownRows, type Category } from "./DemandTable.tsx";
import { CategoryTabs } from "../components/CategoryTabs.tsx";
import type { DemandTableResponse } from "@march/shared";
import { useQueries } from "@tanstack/react-query";
import { CategoryNotes } from "../components/CategoryNotes.tsx";
import { ExportButton, SearchField } from "../components/ListBar.tsx";
import { theme } from "../theme.ts";
import type {
  HistoryResponse,
  HistoryTransactionsResponse,
} from "@march/shared";

const NS_TYPE_LABEL: Record<string, string> = {
  CustInvc: "Invoice",
  CashSale: "Cash Sale",
  CustCred: "Credit Memo",
};

/** Monthly-breakdown columns with hover explanations. */
const MONTH_COLS: { label: string; help?: string }[] = [
  { label: "Month" },
  {
    label: "Sold alone",
    help: "Units sold on their own — revenue-bearing invoice lines for this SKU. This is what NetSuite's Sales by Item report shows, and why that report reads so much lower than the total here.",
  },
  {
    label: "In umbrella orders",
    help: "$0 lines for this SKU on the same invoice as an umbrella that uses it — NetSuite itemises an assembly's components alongside it at zero revenue — plus units shipped free on customer orders. Real units that left the building, but not sales of this SKU.",
  },
  {
    label: "Via umbrella BOM",
    help: "Units where this SKU is a component in a finished good's Bill of Materials — the FG was sold and the SKU is expanded one level down (FG → this SKU). De-duplicated against Direct so the same unit isn't counted twice.",
  },
  {
    label: "L2 fabric",
    help: "Fabric units consumed two BOM levels down: FG → cover assembly → fabric. Fabric SKUs only — always 0 for frames, covers, and bases.",
  },
  {
    label: "Total used",
    help: "Every unit of this SKU that left the building — sold alone, itemised inside umbrella orders, or consumed through an umbrella's bill of materials — each counted once. This is the number for buying the part. It is NOT the same as NetSuite's Sales by Item, which shows only the Sold alone column.",
  },
  {
    label: "Cancelled orders",
    help: "Units customers ordered whose order was then cancelled or closed without shipping — sales asked for and not made. Dated by the order date; umbrella lines are broken down through the BOM like shipped demand. Intercompany orders excluded.",
  },
  {
    label: "Days out of stock",
    help: "Days this month the SKU could not be sold at Jurupa + Texas — on-hand at zero, or NetSuite's Available to Sell at zero. Demand in these months understates what customers wanted.",
  },
];

export function DemandPage() {
  // Two ways to read the same data: the Plan-style per-category table
  // (mirrors the planning workbook's tabs) and the original single-SKU
  // lookup, which is the only one with the month/transaction drill-down.
  // One view switch for the page: three ways to read every part, one part on its own, or the notes.
  type PageView = "overview" | "graph" | "numbers" | "sku" | "notes";
  const [view, setViewState] = useState<PageView>(() => {
    try {
      const v = localStorage.getItem("mi.demand.view");
      return v === "graph" || v === "numbers" ? v : "overview";
    } catch {
      return "overview";
    }
  });
  const setView = (v: PageView) => {
    setViewState(v);
    if (v === "overview" || v === "graph" || v === "numbers") {
      try {
        localStorage.setItem("mi.demand.view", v);
      } catch {
        /* private mode */
      }
    }
  };
  // Every category's table (same cache as DemandTable), for the tab counts.
  const tableQs = useQueries({
    queries: CATEGORIES.map((c) => ({
      queryKey: ["demand-table", c],
      queryFn: () => apiGet<DemandTableResponse>(`/api/demand/table?category=${c}`),
    })),
  });
  const counts = Object.fromEntries(CATEGORIES.map((c, i) => [c, shownRows(tableQs[i].data?.rows ?? [], c).length])) as Record<Category, number>;
  // Owned here, not in DemandTable, so the Notes tab shows the notes for
  // whichever category the table is on.
  const [category, setCategory] = useState<Category>("Frame");

  const [inputSku, setInputSku] = useState("");
  const [querySku, setQuerySku] = useState<string | null>(null);
  // Live mode pulls straight from NetSuite (bypasses the demand_daily
  // cache) — slower, but current and works for SKUs/base patterns not
  // yet backfilled. Included in the query key so toggling refetches.
  const [live, setLive] = useState(false);
  // Which month row is expanded to show its transaction drill-down.
  const [expandedYm, setExpandedYm] = useState<string | null>(null);

  const q = useQuery<HistoryResponse>({
    queryKey: ["demand", querySku, live],
    enabled: querySku !== null,
    queryFn: () =>
      apiGet<HistoryResponse>(
        `/api/history/${encodeURIComponent(querySku!)}${live ? "?live=1" : ""}`,
      ),
  });

  const onLookup = () => {
    if (inputSku.trim()) {
      setQuerySku(inputSku.trim());
      setExpandedYm(null);
    }
  };

  const exportCsv = () => {
    if (!q.data) return;
    downloadCsv(
      `demand-${q.data.itemid}-${new Date().toISOString().slice(0, 10)}.csv`,
      q.data.monthly.map((m) => ({
        Month: m.ym,
        Sold_alone: m.standalone == null ? "" : Math.round(m.standalone),
        In_umbrella_orders: Math.round(m.direct - (m.standalone ?? 0)),
        Via_umbrella_BOM: Math.round(m.l1),
        L2_fabric: Math.round(m.l2),
        Total_used: Math.round(m.total),
        Cancelled_orders: Math.round(q.data!.lost_by_month?.[m.ym] ?? 0),
        Days_out_of_stock: q.data!.oos_by_month == null ? "" : (q.data!.oos_by_month[m.ym] ?? 0),
      })),
    );
  };

  return (
    <div className="u-page">
      <CategoryTabs
        items={view === "sku" ? [] : CATEGORIES}
        value={category}
        onChange={setCategory}
        label={(c) => TAB_LABEL[c]}
        counts={counts}
        loading={tableQs.some((x) => x.isLoading)}
        right={
          <span className="seg" role="group" aria-label="View">
            {([["overview", "Overview"], ["graph", "Compare lines"], ["numbers", "Every part"], ["sku", "One part"]] as const).map(([v, label]) => (
              <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)}>{label}</button>
            ))}
          </span>
        }
      />

      {(view === "overview" || view === "graph" || view === "numbers") && (
        <DemandTable category={category} view={view} onShowNotes={() => setView("notes")} badge={<TieOutBadge />} />
      )}

      {view === "notes" && (
        <div>
          <div className="listbar">
            <h3 className="list-title">Why {TAB_LABEL[category].toLowerCase()} differ from the plan</h3>
            <span className="spacer" />
            <button type="button" className="btn" onClick={() => setView("overview")}>✕ Back to Overview</button>
          </div>
          <CategoryNotes category={category} />
        </div>
      )}

      {view === "sku" && (
        <div style={{ display: "grid", gap: theme.space.lg }}>
      <div className="listbar">
        <h3 className="list-title">One part</h3>
        <SearchField value={inputSku} onChange={setInputSku} onEnter={onLookup} placeholder="Part code, e.g. ALTO908-117" />
        <button type="button" className="btn primary" onClick={onLookup}>Look up</button>
        <label className="check" title="Query NetSuite directly instead of the cached demand table. Slower (~2-5s) but current, and works for SKUs not yet backfilled.">
          <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
          Live from NetSuite
        </label>
        <span className="spacer" />
        {q.data && <ExportButton onClick={exportCsv} />}
      </div>
      <DemandExplainer />

      {q.isLoading && (
        <div style={{ color: theme.color.textMuted }}>
          {live ? "Querying NetSuite… (a few seconds)" : "Loading…"}
        </div>
      )}
      {q.error && (
        <div
          style={{
            padding: theme.space.md,
            background: theme.color.dangerFaint,
            color: theme.color.danger,
            borderRadius: theme.radius.md,
          }}
        >
          {(q.error as Error).message}
        </div>
      )}

      {q.data && (
        <div style={{ display: "grid", gap: theme.space.lg }}>
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: theme.font.size.xl,
                fontWeight: theme.font.weight.semibold,
                display: "flex",
                alignItems: "center",
                gap: theme.space.sm,
                flexWrap: "wrap",
              }}
            >
              <span>{q.data.itemid}</span>
              {q.data.cover_subtype && (
                <span
                  style={{
                    fontSize: theme.font.size.xs,
                    fontWeight: theme.font.weight.semibold,
                    color: theme.color.brand,
                    background: theme.color.brandFaint,
                    padding: "2px 8px",
                    borderRadius: theme.radius.sm,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {q.data.cover_subtype} Cover
                </span>
              )}
              {q.data.live && (
                <span
                  title="Pulled live from NetSuite (not the cached demand table)"
                  style={{
                    fontSize: theme.font.size.xs,
                    fontWeight: theme.font.weight.semibold,
                    color: theme.color.success,
                    background: theme.color.successFaint,
                    padding: "2px 8px",
                    borderRadius: theme.radius.sm,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  ● Live
                </span>
              )}
            </h2>
            <div
              style={{
                fontSize: theme.font.size.sm,
                color: theme.color.textMuted,
                marginTop: 2,
              }}
            >
              Fiscal-year demand · Jul → Jun
            </div>
          </div>

          {/* Fiscal-year totals */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: theme.space.md,
            }}
          >
            {Object.entries(q.data.fy_totals)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([fy, total]) => (
                <div
                  key={fy}
                  style={{
                    padding: theme.space.md,
                    background: theme.color.surface,
                    border: `1px solid ${theme.color.border}`,
                    borderRadius: theme.radius.lg,
                    boxShadow: theme.shadow.sm,
                  }}
                >
                  <div
                    style={{
                      fontSize: theme.font.size.xs,
                      color: theme.color.textMuted,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontWeight: theme.font.weight.medium,
                    }}
                  >
                    {fy}
                    {fy === q.data.current_fy && (
                      <span
                        title="Still accruing — this fiscal year isn't over yet"
                        style={{ color: theme.color.warning, marginLeft: 4 }}
                      >
                        · to date
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: theme.space.xs,
                      fontSize: theme.font.size.xl,
                      fontWeight: theme.font.weight.bold,
                      fontVariantNumeric: "tabular-nums",
                      letterSpacing: -0.5,
                    }}
                  >
                    {fmtNum(total)}
                  </div>
                </div>
              ))}
            {q.data.fy_projection && (
              <div
                key="projection"
                title={`Straight-line trend of ${q.data.fy_projection.basis.join(", ")} — the same =CEILING.MATH(FORECAST.LINEAR(...)) the planning workbook uses. A PROJECTION, not actuals.`}
                style={{
                  padding: theme.space.md,
                  background: theme.color.bg,
                  // Dashed border so it reads as a different KIND of number
                  // from the solid actuals cards beside it.
                  border: `1px dashed ${theme.color.borderStrong}`,
                  borderRadius: theme.radius.lg,
                }}
              >
                <div
                  style={{
                    fontSize: theme.font.size.xs,
                    color: theme.color.textMuted,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    fontWeight: theme.font.weight.medium,
                  }}
                >
                  {q.data.fy_projection.fy} projected
                </div>
                <div
                  style={{
                    marginTop: theme.space.xs,
                    fontSize: theme.font.size.xl,
                    fontWeight: theme.font.weight.bold,
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: -0.5,
                    color: theme.color.textMuted,
                  }}
                >
                  {fmtNum(q.data.fy_projection.value)}
                </div>
                <div
                  style={{
                    fontSize: theme.font.size.xs,
                    color: theme.color.textFaint,
                    marginTop: 2,
                  }}
                >
                  linear trend · {q.data.fy_projection.basis.join(" → ")}
                </div>
              </div>
            )}
            {Object.keys(q.data.fy_totals).length === 0 && (
              <div
                style={{
                  color: theme.color.textMuted,
                  fontSize: theme.font.size.md,
                }}
              >
                No demand for {q.data.itemid}. Backfill may not have reached
                this SKU's months.
              </div>
            )}
          </div>

          {/* Monthly breakdown */}
          <div>
            <h3
              style={{
                margin: `0 0 ${theme.space.sm}px`,
                fontSize: theme.font.size.md,
                fontWeight: theme.font.weight.semibold,
                color: theme.color.textMuted,
              }}
            >
              Monthly breakdown ({q.data.monthly.length} months)
            </h3>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: `2px ${theme.space.md}px`,
                margin: `0 0 ${theme.space.sm}px`,
                fontSize: theme.font.size.xs,
                color: theme.color.textMuted,
                lineHeight: 1.5,
              }}
            >
              {MONTH_COLS.filter((c) => c.help).map((c) => (
                <span key={c.label} style={{ maxWidth: 520 }}>
                  <b style={{ color: theme.color.text }}>{c.label}</b> — {c.help}
                </span>
              ))}
            </div>
            <div
              style={{
                overflow: "auto",
                background: theme.color.surface,
                border: `1px solid ${theme.color.border}`,
                borderRadius: theme.radius.lg,
                boxShadow: theme.shadow.sm,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: theme.font.size.md,
                }}
              >
                <thead style={{ background: theme.color.bg }}>
                  <tr>
                    {MONTH_COLS.map((col, i) => (
                      <th
                        key={col.label}
                        title={col.help}
                        style={{
                          padding: "10px 14px",
                          textAlign: i === 0 ? "left" : "right",
                          borderBottom: `1px solid ${theme.color.border}`,
                          fontWeight: theme.font.weight.semibold,
                          color: theme.color.textMuted,
                          fontSize: theme.font.size.xs,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                          cursor: col.help ? "help" : "default",
                        }}
                      >
                        <span
                          style={
                            col.help
                              ? {
                                  textDecoration: "underline dotted",
                                  textUnderlineOffset: 3,
                                }
                              : undefined
                          }
                        >
                          {col.label}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {q.data.monthly.map((m) => {
                    const open = expandedYm === m.ym;
                    return (
                      <Fragment key={m.ym}>
                        <tr
                          onClick={() => setExpandedYm(open ? null : m.ym)}
                          title="Click to see the invoices / credit memos behind this month"
                          style={{
                            borderBottom: `1px solid ${theme.color.border}`,
                            cursor: "pointer",
                            background: open ? theme.color.brandFaint : undefined,
                          }}
                        >
                          <td style={{ padding: "8px 14px" }}>
                            <span
                              style={{
                                display: "inline-block",
                                width: 14,
                                color: theme.color.textFaint,
                              }}
                            >
                              {open ? "▾" : "▸"}
                            </span>
                            {m.ym}
                          </td>
                          <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {m.standalone == null ? (
                              <span
                                title="Live mode doesn't split this out — switch Live off to see it."
                                style={{ color: theme.color.textFaint }}
                              >
                                —
                              </span>
                            ) : (
                              fmtNum(m.standalone)
                            )}
                          </td>
                          <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {fmtNum(m.standalone == null ? m.direct : m.direct - m.standalone)}
                          </td>
                          <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {fmtNum(m.l1)}
                          </td>
                          <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {fmtNum(m.l2)}
                          </td>
                          <td
                            style={{
                              padding: "8px 14px",
                              textAlign: "right",
                              fontWeight: theme.font.weight.semibold,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {fmtNum(m.total)}
                          </td>
                          <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {(q.data.lost_by_month?.[m.ym] ?? 0) > 0 ? (
                              <span style={{ color: theme.color.dangerText, fontWeight: 600 }}>
                                {fmtNum(Math.round(q.data.lost_by_month![m.ym]))}
                              </span>
                            ) : (
                              <span style={{ color: theme.color.textFaint }}>0</span>
                            )}
                          </td>
                          <td style={{ padding: "8px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                            {q.data.oos_by_month == null ? (
                              <span style={{ color: theme.color.textFaint }}>—</span>
                            ) : (q.data.oos_by_month[m.ym] ?? 0) > 0 ? (
                              <span style={{ color: theme.color.warningText, fontWeight: 600 }}>{q.data.oos_by_month[m.ym]}</span>
                            ) : (
                              <span style={{ color: theme.color.textFaint }}>0</span>
                            )}
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td
                              colSpan={8}
                              style={{
                                padding: 0,
                                borderBottom: `1px solid ${theme.color.border}`,
                                background: theme.color.bg,
                              }}
                            >
                              <TransactionDetail itemid={q.data.itemid} ym={m.ym} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
}

/** Lazy per-month transaction drill-down — always live from NetSuite. */
function TransactionDetail({ itemid, ym }: { itemid: string; ym: string }) {
  const q = useQuery<HistoryTransactionsResponse>({
    queryKey: ["demand-txns", itemid, ym],
    queryFn: () =>
      apiGet<HistoryTransactionsResponse>(
        `/api/history/${encodeURIComponent(itemid)}/transactions?ym=${ym}`,
      ),
    staleTime: 5 * 60_000,
  });

  const pad = `${theme.space.sm}px ${theme.space.lg}px ${theme.space.md}px 34px`;

  if (q.isLoading) {
    return (
      <div style={{ padding: pad, color: theme.color.textMuted, fontSize: theme.font.size.sm }}>
        Querying NetSuite…
      </div>
    );
  }
  if (q.error) {
    return (
      <div style={{ padding: pad, color: theme.color.danger, fontSize: theme.font.size.sm }}>
        {(q.error as Error).message}
      </div>
    );
  }
  const txns = q.data?.transactions ?? [];
  if (txns.length === 0) {
    return (
      <div style={{ padding: pad, color: theme.color.textMuted, fontSize: theme.font.size.sm }}>
        No transactions for {ym}.
      </div>
    );
  }

  const th: React.CSSProperties = {
    padding: "4px 10px",
    textAlign: "left",
    color: theme.color.textMuted,
    fontSize: theme.font.size.xs,
    textTransform: "uppercase",
    letterSpacing: 0.3,
    fontWeight: theme.font.weight.semibold,
    borderBottom: `1px solid ${theme.color.border}`,
  };
  const td: React.CSSProperties = { padding: "4px 10px", verticalAlign: "top" };

  return (
    <div style={{ padding: pad }}>
      <div style={{ fontSize: theme.font.size.xs, color: theme.color.textMuted, marginBottom: 6 }}>
        {txns.length} transaction{txns.length === 1 ? "" : "s"} · live from NetSuite
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: theme.font.size.sm }}>
        <thead>
          <tr>
            <th style={th}>Txn #</th>
            <th style={th}>Type</th>
            <th style={th}>Date</th>
            <th style={th}>Customer</th>
            <th style={th}>Attribution</th>
            <th style={{ ...th, textAlign: "right" }}>Qty</th>
          </tr>
        </thead>
        <tbody>
          {txns.map((t, i) => (
            <tr key={`${t.tranid}-${t.source}-${t.fg ?? ""}-${i}`}>
              <td style={{ ...td, fontWeight: theme.font.weight.medium }}>{t.tranid}</td>
              <td style={td}>{NS_TYPE_LABEL[t.type] ?? t.type}</td>
              <td style={{ ...td, fontVariantNumeric: "tabular-nums", color: theme.color.textMuted }}>
                {t.trandate}
              </td>
              <td style={td}>{t.customer}</td>
              <td style={{ ...td, color: theme.color.textMuted }}>
                {t.source === "Direct" ? "Direct line" : `via ${t.fg ?? "FG"}`}
                {t.source === "L2" ? " (L2)" : ""}
              </td>
              <td
                style={{
                  ...td,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: theme.font.weight.semibold,
                  color: t.qty < 0 ? theme.color.danger : theme.color.text,
                }}
              >
                {fmtNum(t.qty)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
